import { Formik } from 'formik';
import { gql } from '@apollo/client';
import { useParams } from 'react-router';
import * as yup from 'yup';
import { UncontrolledTooltip } from 'reactstrap';
import { useDataScope, useRecord } from '../../utils/hooks';
import { captureEvent } from '../../utils/analytics';
import { useSetBreadcrumbEffect } from '../../contexts/BreadcrumbContext';
import {
  DeleteButton,
  Message,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from './template-components';
import {
  CreatePricingMutation,
  CreatePricingResourceMutation,
  DeletePricingMutation,
  DeletePricingResourceMutation,
  UpdatePricingMutation,
  UpdatePricingResourceMutation,
} from '../../mutations';
import { INPUT_TYPE_FUEL } from '../../contexts/InputTypeContext';
import { asQuantityWithUnit } from '../../utils/formatters';
import UnderlinedLink from '../basics/UnderlinedLink';
import ExpandableItemsList from '../ExpandableItemsList';

export const FUEL_PRICING_TYPE = 'fuel';
export const LABOR_PRICING_TYPE = 'labor';

const defaultPricing = (type) => ({
  name: '',
  type,
  pricePerUnit: 1.0,
});

const PricingFields = `
  id
  updatedAt
  type
  name
  pricePerUnit
  inUse
`;

const PricingResourceQuery = gql`
  query PricingResource($id: ID!) {
    pricingResource(id: $id) {
      ${PricingFields}
      referencedByCapexes {
        id
        name
      }
    }
  }
`;

const RawPricingQuery = gql`
  query RawPricing($id: ID!) {
    rawPricing(id: $id) {
      ${PricingFields}
      referencedByCapexes {
        id
        name
      }
    }
  }
`;

const pricingSchema = yup.object().shape({
  name: yup.string().required('Required'),
  pricePerUnit: yup.number().min(0).required('Required'),
});

const formatCapexLink = (capex) => (
  <UnderlinedLink to={`../../capexes/${capex.id}/edit`}>
    {capex.name}
  </UnderlinedLink>
);

const EditPricing = ({
  onSubmit,
  onDelete,
  status,
  msg,
  resource,
  basePath,
  pricingType,
  hideDelete,
  unit,
  unitSpec,
  // function that takes in a pricing name, and determines if it should be editable
  canEditName = () => true,
}) => {
  const { loading } = status;
  const { id, teamId } = useParams();
  const pricing = resource || defaultPricing(pricingType);
  const scope = useDataScope();

  const breadcrumbs = [{ display: id ? pricing.name || '...' : 'New' }];
  if (pricingType === FUEL_PRICING_TYPE) {
    breadcrumbs.unshift({ display: INPUT_TYPE_FUEL, url: basePath });
  }
  useSetBreadcrumbEffect(breadcrumbs, [id, pricing.name, pricingType, teamId]);

  // Only add teamId if we are creating a pricing, cannot update the team after creation
  const initialValues = id ? pricing : { teamId, ...pricing };

  const { inUse } = pricing;

  if (msg) return <Message>{msg}</Message>;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={pricingSchema}
      onSubmit={onSubmit(pricingSchema)}
    >
      {({ isSubmitting, values }) => (
        <TemplateForm>
          <ValidatedInput
            type="text"
            name="name"
            label="Name"
            disabled={!canEditName(values.name)}
          />
          <ValidatedInput
            type="number"
            name="pricePerUnit"
            label="Price"
            min={0}
            step={0.01}
            unit={unit}
            unitSpec={unitSpec}
            currency
            maxWidth="150px"
          />
          {pricingType === FUEL_PRICING_TYPE && id && (
            <div className="mb-3">
              <ExpandableItemsList
                items={pricing.referencedByCapexes}
                title={`In use on ${asQuantityWithUnit(
                  'capexes',
                  'capex',
                )(pricing.referencedByCapexes?.length)}`}
                formatItem={formatCapexLink}
                onToggle={(newToggleState) => {
                  // only care about recording an event when someone opens the list
                  if (newToggleState) {
                    captureEvent('fuel-reference-list-expanded', {
                      scope,
                    });
                  }
                }}
              />
            </div>
          )}
          {id && !hideDelete && (
            <>
              <DeleteButton
                id="pricing-delete-button"
                onDelete={onDelete}
                disabled={isSubmitting || inUse || loading}
                className="mr-2"
              />
              {inUse && (
                <UncontrolledTooltip target="pricing-delete-button">
                  Cannot delete if in use
                </UncontrolledTooltip>
              )}
            </>
          )}
          <SubmitButton loading={loading} />
        </TemplateForm>
      )}
    </Formik>
  );
};

const transformValues = (values) => ({
  ...values,
  __typename: undefined,
  referencedByCapexes: undefined,
  inUse: undefined,
});

export const EditFarmPricing = ({
  onDeleteRefetchQuery,
  basePath,
  ...props
}) => {
  const { id } = useParams();
  const params = useRecord({
    submitMutation: id ? UpdatePricingMutation : CreatePricingMutation,
    deleteMutation: DeletePricingMutation,
    fetchRecordQuery: RawPricingQuery,
    onDeleteRefetchQuery,
    creationRedirectUrl: `${basePath}/pricing/:id/edit`,
    deletionRedirectUrl: basePath,
    includeFarmId: !id,
    transformValues,
  });

  return <EditPricing {...params} basePath={basePath} {...props} />;
};

export const EditPricingResource = ({ onDeleteRefetchQuery, ...props }) => {
  const { id } = useParams();
  const params = useRecord({
    submitMutation: id
      ? UpdatePricingResourceMutation
      : CreatePricingResourceMutation,
    deleteMutation: DeletePricingResourceMutation,
    fetchRecordQuery: PricingResourceQuery,
    onDeleteRefetchQuery,
    creationRedirectUrl: '../:id/edit',
    deletionRedirectUrl: '../../..',
    transformValues,
  });

  return <EditPricing {...params} {...props} />;
};
